import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useRegions } from './useRegions';
import { useDivisions } from './useDivisions';
import { useIsMsp } from './useIsMsp';
// Through the feature barrel, which the trial build swaps for its teaser, so
// the MSP API client never reaches the trial bundle (as Divisions.tsx does).
import { useMspCustomers } from '@/features/msp';

/**
 * Where something belongs: a region, and the division it sits in if any.
 *
 * The region is what decides placement (its gateways poll, its devices land
 * there); the division is context. In an MSP organization a division may be a
 * customer, which is shown as a tag rather than by changing the words, so the
 * same "Belongs to" reads right for an MSP and for everyone else.
 */
export interface Placement {
  regionId: string;
  regionName: string;
  divisionId: string | null;
  divisionName: string | null;
  /** The division is one of this MSP's customers. */
  isCustomer: boolean;
  /** "Division › Region", or just the region when it has no division. */
  path: string;
  /** The name a thing placed here is called after: the division, else the region. */
  owner: string;
}

export function usePlacements(): {
  placements: Placement[];
  placementOf: (regionId: string | null | undefined) => Placement | undefined;
} {
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const isMsp = useIsMsp();
  const { data: regions } = useRegions(organizationId);
  const { data: divisions } = useDivisions(organizationId);
  const { data: customers } = useMspCustomers(organizationId, isMsp);

  return useMemo(() => {
    const customerIds = new Set((customers?.customers ?? []).map((c) => c.divisionId));
    const placements: Placement[] = (regions ?? []).map((region) => {
      const division = divisions?.find((d) => d.id === region.divisionId) ?? null;
      return {
        regionId: region.id,
        regionName: region.name,
        divisionId: division?.id ?? null,
        divisionName: division?.name ?? null,
        isCustomer: !!division && customerIds.has(division.id),
        path: division ? `${division.name} › ${region.name}` : region.name,
        owner: division?.name ?? region.name,
      };
    });
    const byRegion = new Map(placements.map((p) => [p.regionId, p]));
    return {
      placements,
      placementOf: (regionId) => (regionId ? byRegion.get(regionId) : undefined),
    };
  }, [regions, divisions, customers]);
}
